import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import swal from "sweetalert2";
import { CatalogosIAService } from "../catalogos-ia.service";
import {
  CatCicloVida,
  CatNivelSupervision,
  CatTipoImpacto,
} from "src/app/shared/model/iso42001/inventario-ia.model";

type Tab = "ciclo" | "nivel" | "impacto";

@Component({
  selector: "app-catalogos-inventario-ia",
  standalone: true,
  templateUrl: "./catalogos-inventario-ia.component.html",
  styleUrls: ["./catalogos-inventario-ia.component.css"],
  imports: [CommonModule, FormsModule],
})
export class CatalogosInventarioIAComponent implements OnInit {
  tab: Tab = "ciclo";
  error = "";
  guardando = false;

  // Paginación (10 por página, por pestaña); se ocultan los inactivos
  tamPagina = 10;
  paginaActual = 1;

  ciclos: CatCicloVida[] = [];
  niveles: CatNivelSupervision[] = [];
  impactos: CatTipoImpacto[] = [];

  cicloEdit: CatCicloVida = this.nuevoCiclo();
  nivelEdit: CatNivelSupervision = this.nuevoNivel();
  impactoEdit: CatTipoImpacto = this.nuevoImpacto();

  constructor(
    private service: CatalogosIAService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarCiclos();
    this.cargarNiveles();
    this.cargarImpactos();
  }

  cambiarTab(t: Tab): void {
    this.tab = t;
    this.paginaActual = 1;
    this.error = "";
  }

  // ===================== Activos + Paginación =====================
  ciclosActivos(): CatCicloVida[] { return this.ciclos.filter((c) => c.estatus === "ACTIVO"); }
  nivelesActivos(): CatNivelSupervision[] { return this.niveles.filter((n) => n.estatus === "ACTIVO"); }
  impactosActivos(): CatTipoImpacto[] { return this.impactos.filter((i) => i.estatus === "ACTIVO"); }

  private listaActivaActual(): unknown[] {
    if (this.tab === "ciclo") { return this.ciclosActivos(); }
    if (this.tab === "nivel") { return this.nivelesActivos(); }
    return this.impactosActivos();
  }

  totalActual(): number { return this.listaActivaActual().length; }

  totalPaginas(): number {
    const n = this.totalActual();
    return n > 0 ? Math.ceil(n / this.tamPagina) : 1;
  }

  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  rangoInicio(): number {
    return this.totalActual() === 0 ? 0 : (this.paginaNorm() - 1) * this.tamPagina + 1;
  }

  rangoFin(): number {
    return Math.min(this.paginaNorm() * this.tamPagina, this.totalActual());
  }

  ciclosPagina(): CatCicloVida[] {
    const i = (this.paginaNorm() - 1) * this.tamPagina;
    return this.ciclosActivos().slice(i, i + this.tamPagina);
  }
  nivelesPagina(): CatNivelSupervision[] {
    const i = (this.paginaNorm() - 1) * this.tamPagina;
    return this.nivelesActivos().slice(i, i + this.tamPagina);
  }
  impactosPagina(): CatTipoImpacto[] {
    const i = (this.paginaNorm() - 1) * this.tamPagina;
    return this.impactosActivos().slice(i, i + this.tamPagina);
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) { return; }
    this.paginaActual = p;
    this.cdr.detectChanges();
  }
  primeraPagina(): void { this.irPagina(1); }
  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }
  ultimaPagina(): void { this.irPagina(this.totalPaginas()); }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPaginas();
    if (total <= 1) { return [1]; }
    const a = this.paginaNorm();
    const set = new Set<number>([1, total, a - 1, a, a + 1]);
    const ord = Array.from(set).filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
    const items: Array<number | string> = [];
    for (let k = 0; k < ord.length; k++) {
      const p = ord[k];
      const prev = ord[k - 1];
      if (prev && p - prev > 1) { items.push("e" + prev); }
      items.push(p);
    }
    return items;
  }
  isPageNumber(item: number | string): item is number { return typeof item === "number"; }

  // ===================== Eliminar (baja lógica con confirmación) =====================
  eliminarCiclo(c: CatCicloVida): void {
    this.confirmarEliminar(c.nombre, () =>
      this.service.cambiarEstatusCiclo(c.idCicloVida!, "INACTIVO").subscribe(() => this.cargarCiclos()));
  }
  eliminarNivel(n: CatNivelSupervision): void {
    this.confirmarEliminar(n.nombre, () =>
      this.service.cambiarEstatusNivel(n.idNivelSupervision!, "INACTIVO").subscribe(() => this.cargarNiveles()));
  }
  eliminarImpacto(i: CatTipoImpacto): void {
    this.confirmarEliminar(i.nombre, () =>
      this.service.cambiarEstatusImpacto(i.idTipoImpacto!, "INACTIVO").subscribe(() => this.cargarImpactos()));
  }

  private confirmarEliminar(nombre: string, accion: () => void): void {
    void swal.fire({
      icon: "warning",
      title: "Eliminar registro",
      text: `Se eliminará "${nombre}". ¿Deseas continuar?`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c8102e",
    }).then((r) => { if (r.isConfirmed) { accion(); } });
  }

  // ===================== Ciclo de Vida =====================
  cargarCiclos(): void {
    this.service.listarCiclos().subscribe({
      next: (r) => { this.ciclos = r.ciclosVida || []; this.cdr.detectChanges(); },
      error: () => { this.ciclos = []; this.cdr.detectChanges(); },
    });
  }
  editarCiclo(c: CatCicloVida): void {
    this.cicloEdit = { ...c };
    this.error = "";
  }
  cancelarCiclo(): void {
    this.cicloEdit = this.nuevoCiclo();
    this.error = "";
  }
  guardarCiclo(): void {
    if (!this.cicloEdit.nombre?.trim()) { this.error = "El nombre es obligatorio."; return; }
    this.guardando = true;
    this.service.guardarCiclo(this.cicloEdit).subscribe({
      next: (r) => {
        this.guardando = false;
        if (r.codigoError) { this.error = r.mensajeError || "Error al guardar"; this.cdr.detectChanges(); return; }
        this.cancelarCiclo();
        this.cargarCiclos();
      },
      error: () => { this.guardando = false; this.error = "Error al guardar el ciclo de vida."; this.cdr.detectChanges(); },
    });
  }
  estatusCiclo(c: CatCicloVida): void {
    const nuevo = c.estatus === "ACTIVO" ? "INACTIVO" : "ACTIVO";
    this.service.cambiarEstatusCiclo(c.idCicloVida!, nuevo).subscribe(() => this.cargarCiclos());
  }

  // ===================== Nivel de Supervisión =====================
  cargarNiveles(): void {
    this.service.listarNiveles().subscribe({
      next: (r) => { this.niveles = r.nivelesSupervision || []; this.cdr.detectChanges(); },
      error: () => { this.niveles = []; this.cdr.detectChanges(); },
    });
  }
  editarNivel(n: CatNivelSupervision): void {
    this.nivelEdit = { ...n };
    this.error = "";
  }
  cancelarNivel(): void {
    this.nivelEdit = this.nuevoNivel();
    this.error = "";
  }
  guardarNivel(): void {
    if (!this.nivelEdit.nombre?.trim()) { this.error = "El nombre es obligatorio."; return; }
    this.guardando = true;
    this.service.guardarNivel(this.nivelEdit).subscribe({
      next: (r) => {
        this.guardando = false;
        if (r.codigoError) { this.error = r.mensajeError || "Error al guardar"; this.cdr.detectChanges(); return; }
        this.cancelarNivel();
        this.cargarNiveles();
      },
      error: () => { this.guardando = false; this.error = "Error al guardar el nivel de supervisión."; this.cdr.detectChanges(); },
    });
  }
  estatusNivel(n: CatNivelSupervision): void {
    const nuevo = n.estatus === "ACTIVO" ? "INACTIVO" : "ACTIVO";
    this.service.cambiarEstatusNivel(n.idNivelSupervision!, nuevo).subscribe(() => this.cargarNiveles());
  }

  // ===================== Tipo de Impacto =====================
  cargarImpactos(): void {
    this.service.listarImpactos().subscribe({
      next: (r) => { this.impactos = r.tiposImpacto || []; this.cdr.detectChanges(); },
      error: () => { this.impactos = []; this.cdr.detectChanges(); },
    });
  }
  editarImpacto(i: CatTipoImpacto): void {
    this.impactoEdit = { ...i };
    this.error = "";
  }
  cancelarImpacto(): void {
    this.impactoEdit = this.nuevoImpacto();
    this.error = "";
  }
  guardarImpacto(): void {
    if (!this.impactoEdit.nombre?.trim()) { this.error = "El nombre es obligatorio."; return; }
    this.guardando = true;
    this.service.guardarImpacto(this.impactoEdit).subscribe({
      next: (r) => {
        this.guardando = false;
        if (r.codigoError) { this.error = r.mensajeError || "Error al guardar"; this.cdr.detectChanges(); return; }
        this.cancelarImpacto();
        this.cargarImpactos();
      },
      error: () => { this.guardando = false; this.error = "Error al guardar el tipo de impacto."; this.cdr.detectChanges(); },
    });
  }
  estatusImpacto(i: CatTipoImpacto): void {
    const nuevo = i.estatus === "ACTIVO" ? "INACTIVO" : "ACTIVO";
    this.service.cambiarEstatusImpacto(i.idTipoImpacto!, nuevo).subscribe(() => this.cargarImpactos());
  }

  // ===================== Helpers =====================
  private nuevoCiclo(): CatCicloVida { return { nombre: "", descripcion: "", orden: 0, estatus: "ACTIVO" }; }
  private nuevoNivel(): CatNivelSupervision { return { nombre: "", descripcion: "", estatus: "ACTIVO" }; }
  private nuevoImpacto(): CatTipoImpacto { return { nombre: "", sugerenciaPositivo: "", sugerenciaNegativo: "", estatus: "ACTIVO" }; }
}
